<template>
  <form class="vl-form" @submit.prevent="onSubmit">
    <vl-grid v-if="!isLoading" v-vl-spacer:bottom.medium>
      <vl-column v-vl-spacer:bottom.medium width="12" width-s="12" width-xs="12">
        <PbsRadioField name="specificationType" :values="specificationTypes" />
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.large>
          <PbsSelectField
            v-if="isLevering"
            name="deliveryProcessDefinition"
            :label="t('specificationImport.deliveryprocessdefinition')"
            :placeholder="$t('specificationImport.deliveryprocessdefinition')"
            :options="deliveryProcessDefinitions"
            :custom-label="getDeliveryProcessDefinitionLabel"
            :max-height="maxHeightDropdown"
          />
          <PbsSelectField
            v-if="isProduct"
            name="productProcessDefinition"
            :label="t('specificationImport.productprocessdefinition')"
            :placeholder="$t('specificationImport.productprocessdefinition')"
            :options="productProcessDefinitions"
            :custom-label="getProductProcessDefinitionLabel"
            :max-height="maxHeightDropdown"
          />
        </vl-form-column>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{ $t('specificationImport.import') }}</vl-button>
          <vl-button :mod-disabled="isSaving" mod-icon-before mod-secondary tag-name="a" @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import * as yup from 'yup';
import { PropType, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import {
  DeliveryProcessDefinitionDetailDto,
  ImportSpecificationDto,
  ImportSpecificationType,
  ProductProcessDefinitionDetailDto
} from '@/api/backoffice/clients';
import { useDeliveryProcessDefinitionListStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-list.store';
import { useProductProcessDefinitionListStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-list.store';
import { getDeliveryProcessDefinitionLabel } from '@/modules/configuration/common/delivery-process-definition.common';
import { getProductProcessDefinitionLabel } from '@/modules/configuration/common/product-process-definition.common';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsRadioField, { PbsRadioFieldValue } from '@/components/forms/PbsRadioField.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { parseException } from '@/modules/core/store/alert.store';

const { t } = useI18n();

const maxHeightDropdown = 175;

const props = defineProps({
  definitionId: {
    type: Number,
    required: true
  },
  target: {
    type: Number as PropType<ImportSpecificationType>,
    required: true
  }
});

const deliveryProcessDefinitionDetailStore = useDeliveryProcessDefinitionDetailStore();
const productProcessDefinitionDetailStore = useProductProcessDefinitionDetailStore();
const deliveryProcessDefinitionStore = useDeliveryProcessDefinitionListStore();
const productProcessDefinitionStore = useProductProcessDefinitionListStore();

const validationSchema = yup.object({
  specificationType: yup.string().required(),
  deliveryProcessDefinition: yup
    .object()
    .nullable()
    .when('specificationType', {
      is: (specificationType) =>
        specificationType == ImportSpecificationType.DeliverySpecification ||
        specificationType == ImportSpecificationType.OperationSpecification,
      then: (s) =>
        s.required(
          t('validations.messages.required', {
            _field_: t('specificationImport.deliveryprocessdefinition')
          })
        ),
      otherwise: (s) => s.optional()
    }),
  productProcessDefinition: yup
    .object()
    .nullable()
    .when('specificationType', {
      is: (specificationType) =>
        specificationType == ImportSpecificationType.ProductSpecification ||
        specificationType == ImportSpecificationType.SourceSpecification,
      then: (s) =>
        s.required(
          t('validations.messages.required', {
            _field_: t('specificationImport.productprocessdefinition')
          })
        ),
      otherwise: (s) => s.optional()
    })
});

const specificationTypes: PbsRadioFieldValue[] = [
  {
    value: ImportSpecificationType.DeliverySpecification,
    label: t('specificationImport.deliverySpecification')
  },
  {
    value: ImportSpecificationType.OperationSpecification,
    label: t('specificationImport.operationSpecification')
  },
  {
    value: ImportSpecificationType.ProductSpecification,
    label: t('specificationImport.productSpecification')
  },
  {
    value: ImportSpecificationType.SourceSpecification,
    label: t('specificationImport.sourceSpecification')
  }
];

const { handleSubmit, values, resetForm } = useForm({
  validationSchema
});

const data = reactive({
  deliveryProcessDefinitions: [] as DeliveryProcessDefinitionDetailDto[],
  productProcessDefinitions: [] as ProductProcessDefinitionDetailDto[],
  isSaving: false,
  isLoading: false,
  error: null
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await Promise.all([loadDeliveryProcessDefinitions(), loadProductProcessDefinitions()]);
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  resetForm({
    values: {
      deliveryprocessdefinition: null,
      productProcessDefinition: null,
      specificationType: ImportSpecificationType.DeliverySpecification
    }
  });
});

const loadDeliveryProcessDefinitions = async () => {
  data.deliveryProcessDefinitions = await deliveryProcessDefinitionStore.getAll();
};

const loadProductProcessDefinitions = async () => {
  data.productProcessDefinitions = await productProcessDefinitionStore.getAll();
};

const isLevering = computed(() => {
  return (
    values.specificationType === ImportSpecificationType.DeliverySpecification ||
    values.specificationType === ImportSpecificationType.OperationSpecification
  );
});

const isProduct = computed(() => {
  return (
    values.specificationType === ImportSpecificationType.ProductSpecification ||
    values.specificationType === ImportSpecificationType.SourceSpecification
  );
});

const isTargetLevering = computed(() => {
  return (
    props.target === ImportSpecificationType.DeliverySpecification ||
    props.target === ImportSpecificationType.OperationSpecification
  );
});

const isTargetProduct = computed(() => {
  return (
    props.target === ImportSpecificationType.ProductSpecification ||
    props.target === ImportSpecificationType.SourceSpecification
  );
});

const deliveryProcessDefinitions = computed(() => {
  return data.deliveryProcessDefinitions;
});

const productProcessDefinitions = computed(() => {
  return data.productProcessDefinitions;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const error = computed(() => {
  return data.error;
});

const onSuccess = async (values) => {
  try {
    data.isSaving = true;
    const importSpecificationDto = createDto(values);
    if (isTargetLevering.value) {
      await deliveryProcessDefinitionDetailStore.importSpecification(props.definitionId, importSpecificationDto);
    } else if (isTargetProduct.value) {
      await productProcessDefinitionDetailStore.importSpecification(props.definitionId, importSpecificationDto);
    }
  } finally {
    data.isSaving = false;
  }

  closeModal();
};

const createDto = (values): ImportSpecificationDto => {
  const dto: ImportSpecificationDto = {
    target: props.target
  };

  if (values.deliveryProcessDefinition) {
    dto.deliveryProcessDefinition = {
      definitionId: values.deliveryProcessDefinition.id,
      specificationType: values.specificationType
    };
  }

  if (values.productProcessDefinition) {
    dto.productProcessDefinition = {
      definitionId: values.productProcessDefinition.id,
      specificationType: values.specificationType
    };
  }

  return dto;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
